// =============================================================================
// src/lib/shellCountsCore.js - pure helpers behind lib/shellCounts.js (no
// Supabase, no React), so node --test can cover them. Round 4 #3 / #9.
// =============================================================================

// ---- pure helpers (unit tested) -------------------------------------------------
// The one number the badge, the bell and the Inbox subtitle show: open items
// across the practice (inbox_feed_counts.all). null while unknown.
export function inboxOpenCount(counts) {
  if (!counts || counts.all == null) return null;
  const n = Number(counts.all);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

// Total open things in an eod_sweep result (what the card would list).
export function eodOpenCount(sweep) {
  const sections = (sweep && Array.isArray(sweep.sections)) ? sweep.sections : [];
  return sections.reduce((n, s) => n + (Number(s && s.count) || 0), 0);
}

// Hour (0-23) and YYYY-MM-DD in an IANA zone; falls back to the device clock
// when the zone is missing or unknown. The end-of-day nudge keys on the
// practice's day, not the device's (a laptop on another coast).
export function zonedClock(now, timeZone) {
  const d = now instanceof Date ? now : new Date(now);
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23" })
        .formatToParts(d).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
      const hour = Number(parts.hour) % 24;
      if (parts.year && Number.isFinite(hour)) return { hour, day: parts.year + "-" + parts.month + "-" + parts.day };
    } catch (e) { /* unknown zone: device clock below */ }
  }
  return {
    hour: d.getHours(),
    day: d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"),
  };
}

// The nudge line after "Before you go:", e.g. "3 things are still open today".
export function eodNudgeText(n) {
  return n === 1 ? "1 thing is still open today" : n + " things are still open today";
}

// ---- round 7 #2: one shell read --------------------------------------------------
// The badges shell_counts() can answer. Each is also readable on its own (the
// per-badge read in lib/shellCounts.js), which is what runs before the
// migration is applied or when a key is missing from the answer.
export const SHELL_KEYS = ["inbox", "insurance_updates", "waitlist", "billing", "unsigned_notes", "tasks",
  "ready_for_you", "inbound_sms", "system_alerts", "ride_status"];

// Roles that drive care team rides (ridesCore ALL_DRIVER_ROLES and the
// database's _ride_is_driver_role); kept here so this file stays import-free.
const DRIVER_ROLES = ["Care Manager", "Supervising Care Manager", "Care Manager Supervisor", "Owner", "Manager", "CHW"];

// Which badges this shell shows, with the gates the sidebar, the bell and the
// banners use: the role's nav ids (navIds, a Set or an array), the
// Documents-only Inbox (docsOnlyRoles), a provider for "Ready for you", the
// Inbound SMS feature, the Pro / Command tier for system alerts and the
// driver roles for ride status. Before the migration is applied this is the
// list of per-badge reads the one poll makes.
export function shellWants({ navIds, role = null, profileRole = null, uid = null, providerId = null,
  inboundSms = false, tier = null, docsOnlyRoles = [] } = {}) {
  const has = (id) => !!navIds && (typeof navIds.has === "function" ? navIds.has(id) : navIds.includes(id));
  const out = [];
  if (has("inbox") && !docsOnlyRoles.includes(role) && !docsOnlyRoles.includes(profileRole)) out.push("inbox");
  if (has("insurance_updates")) out.push("insurance_updates");
  if (has("waitlist")) out.push("waitlist");
  if (has("billing")) out.push("billing");
  if (has("clinical")) out.push("unsigned_notes");
  if (has("tasks") && uid) out.push("tasks");
  if (has("queue") && providerId) out.push("ready_for_you");
  if (inboundSms) out.push("inbound_sms");
  if (tier === "Pro" || tier === "Command") out.push("system_alerts");
  if (DRIVER_ROLES.includes(role)) out.push("ride_status");
  return out;
}

// The function is not there yet (before the migration is applied): PostgREST
// answers PGRST202 ("Could not find the function ... in the schema cache"),
// HTTP 404, or Postgres 42883. Anything else is a real failure.
export function isMissingFunction(error) {
  if (!error) return false;
  const code = String(error.code || "");
  if (code === "PGRST202" || code === "42883") return true;
  if (Number(error.status) === 404) return true;
  return /schema cache|could not find the function/i.test(String(error.message || ""));
}

// A usable shell_counts() answer is one JSON object. Anything else (null, an
// array) is treated as "not available", so the per-badge reads take over.
export function isShellAnswer(data) {
  return !!data && typeof data === "object" && !Array.isArray(data);
}

// The value one shell read has for `key`, or undefined when it has none (the
// badge then reads itself, once).
export function shellValueOf(result, key) {
  const v = result && result.values;
  if (!v || !Object.prototype.hasOwnProperty.call(v, key) || v[key] == null) return undefined;
  return v[key];
}
